import React from 'react';
import {
    GripVertical,
    Trash2,
    Database,
    BarChart3
} from 'lucide-react';
import {
    DigestSectionDTO,
    SavedReportDTO,
    AvailableMetricDTO
} from '../../../../../services/claudeQueryService';
import { useTranslation } from '../../../../../contexts/I18nProvider';

interface DigestSectionEditorProps {
    section: DigestSectionDTO;
    index: number;
    reports: SavedReportDTO[];
    metrics: AvailableMetricDTO[];
    onChange: (index: number, section: DigestSectionDTO) => void;
    onRemove: (index: number) => void;
    onMoveUp: (index: number) => void;
    onMoveDown: (index: number) => void;
    isFirst: boolean;
    isLast: boolean;
}

export const DigestSectionEditor: React.FC<DigestSectionEditorProps> = ({
    section,
    index,
    reports,
    metrics,
    onChange,
    onRemove,
    onMoveUp,
    onMoveDown,
    isFirst,
    isLast
}) => {
    const { t } = useTranslation();
    const handleTypeChange = (newType: string) => {
        onChange(index, {
            ...section,
            sectionType: newType,
            sourceId: '',
            title: '',
            maxRows: newType === 'saved-report' ? 20 : undefined
        });
    };

    const handleSourceChange = (sourceId: string) => {
        // Auto-set title from source
        let autoTitle = '';
        if (section.sectionType === 'saved-report') {
            const report = reports.find(r => r.reportId.toString() === sourceId);
            autoTitle = report?.name || '';
        } else {
            const metric = metrics.find(m => m.key === sourceId);
            autoTitle = metric?.name || '';
        }

        onChange(index, {
            ...section,
            sourceId,
            title: section.title || autoTitle
        });
    };

    return (
        <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200 group">
            {/* Drag handle + reorder */}
            <div className="flex flex-col items-center gap-1 pt-1">
                <GripVertical size={16} className="text-gray-300" />
                <button
                    onClick={() => onMoveUp(index)}
                    disabled={isFirst}
                    className="text-xs text-gray-400 hover:text-blue-500 disabled:opacity-30 disabled:cursor-not-allowed"
                    title={t('manager.modules.claudeQuery.digests.moveUp', {}, 'Flytt opp')}
                >
                    ▲
                </button>
                <button
                    onClick={() => onMoveDown(index)}
                    disabled={isLast}
                    className="text-xs text-gray-400 hover:text-blue-500 disabled:opacity-30 disabled:cursor-not-allowed"
                    title={t('manager.modules.claudeQuery.digests.moveDown', {}, 'Flytt ned')}
                >
                    ▼
                </button>
            </div>

            {/* Content */}
            <div className="flex-1 space-y-2">
                {/* Row 1: Type + Source */}
                <div className="flex items-center gap-2">
                    {/* Section type */}
                    <div className="flex items-center gap-1.5">
                        {section.sectionType === 'saved-report' ? (
                            <Database size={14} className="text-blue-500" />
                        ) : (
                            <BarChart3 size={14} className="text-green-500" />
                        )}
                        <select
                            value={section.sectionType}
                            onChange={(e) => handleTypeChange(e.target.value)}
                            className="text-sm border border-gray-200 rounded px-2 py-1.5 bg-white
                                     focus:outline-none focus:border-blue-400"
                        >
                            <option value="dashboard-metric">{t('manager.modules.claudeQuery.digests.typeDashboardMetric', {}, 'Dashboard-metrikk')}</option>
                            <option value="saved-report">{t('manager.modules.claudeQuery.digests.typeSavedReport', {}, 'Lagret rapport')}</option>
                        </select>
                    </div>

                    {/* Source picker */}
                    <select
                        value={section.sourceId}
                        onChange={(e) => handleSourceChange(e.target.value)}
                        className="flex-1 text-sm border border-gray-200 rounded px-2 py-1.5 bg-white
                                 focus:outline-none focus:border-blue-400"
                    >
                        <option value="">{t('manager.modules.claudeQuery.digests.selectSource', {}, '— Velg kilde —')}</option>
                        {section.sectionType === 'saved-report' ? (
                            reports.map(r => (
                                <option key={r.reportId} value={r.reportId.toString()}>
                                    {r.name}
                                </option>
                            ))
                        ) : (
                            metrics.map(m => (
                                <option key={m.key} value={m.key}>
                                    {m.name} — {m.description}
                                </option>
                            ))
                        )}
                    </select>
                </div>

                {/* Row 2: Title override + maxRows */}
                <div className="flex items-center gap-2">
                    <input
                        type="text"
                        value={section.title || ''}
                        onChange={(e) => onChange(index, { ...section, title: e.target.value })}
                        placeholder={t('manager.modules.claudeQuery.digests.titleOverridePlaceholder', {}, 'Tittel-override (valgfritt)')}
                        className="flex-1 text-sm border border-gray-200 rounded px-2 py-1.5
                                 focus:outline-none focus:border-blue-400 placeholder-gray-300"
                    />
                    {section.sectionType === 'saved-report' && (
                        <>
                            <div className="flex items-center gap-1">
                                <label className="text-xs text-gray-400 whitespace-nowrap">{t('manager.modules.claudeQuery.digests.maxRows', {}, 'Maks rader:')}</label>
                                <input
                                    type="number"
                                    value={section.maxRows ?? 20}
                                    onChange={(e) => onChange(index, {
                                        ...section,
                                        maxRows: parseInt(e.target.value) || 20
                                    })}
                                    min={1}
                                    max={200}
                                    className="w-16 text-sm border border-gray-200 rounded px-2 py-1.5 text-center
                                             focus:outline-none focus:border-blue-400"
                                />
                            </div>
                            <label className="flex items-center gap-1.5 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={section.includeChart ?? false}
                                    onChange={(e) => onChange(index, {
                                        ...section,
                                        includeChart: e.target.checked
                                    })}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-xs text-gray-500 whitespace-nowrap">
                                    <BarChart3 size={11} className="inline mr-0.5" />
                                    {t('manager.modules.claudeQuery.digests.chart', {}, 'Diagram')}
                                </span>
                            </label>
                        </>
                    )}
                </div>
            </div>

            {/* Delete */}
            <button
                onClick={() => onRemove(index)}
                className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded transition-colors
                         opacity-0 group-hover:opacity-100"
                title={t('manager.modules.claudeQuery.digests.removeSection', {}, 'Fjern seksjon')}
            >
                <Trash2 size={14} />
            </button>
        </div>
    );
};
